import React from "react";

/**
 * Global "Skip to main content" link.
 *
 * - Render this as the very first focusable element in the DOM (inside `<body>`).
 * - Targets `<main id="main-content">`.
 * - Explicitly moves keyboard focus to the main element on activation.
 * - Adds a data attribute to mark skip-link activation for CSS targeting.
 */
export default function SkipToContent() {
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    // Ensure this runs only in the browser
    if (typeof document === "undefined") return;

    // Prevent default navigation behavior that causes scrolling
    event.preventDefault();
    event.stopPropagation();

    const main = document.getElementById("main-content") as HTMLElement | null;

    if (main) {
      // Make sure main is focusable
      if (!main.hasAttribute("tabindex")) {
        main.setAttribute("tabindex", "-1");
      }

      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        // Mark that skip link was activated on main
        main.setAttribute("data-skip-link-active", "true");

        // Calculate header height to account for fixed header
        const header = document.querySelector("header");
        const headerHeight = header ? header.offsetHeight : 0;
        const additionalOffset = 20; // Extra padding for better visibility
        const totalOffset = headerHeight + additionalOffset;

        // Get the element's position relative to the document
        const elementRect = main.getBoundingClientRect();
        const absoluteElementTop = elementRect.top + window.pageYOffset;

        // Calculate scroll position accounting for fixed header
        const scrollPosition = absoluteElementTop - totalOffset;

        // Scroll to the calculated position smoothly
        window.scrollTo({
          top: Math.max(0, scrollPosition),
          behavior: "smooth",
        });

        // Focus the main element after scrolling
        setTimeout(() => {
          main.focus({ preventScroll: true });
        }, 300);

        // Remove the marker function
        const removeMarker = () => {
          if (main.hasAttribute("data-skip-link-active")) {
            main.removeAttribute("data-skip-link-active");
            // Blur the main element to remove default browser focus outline
            main.blur();
          }
        };

        // Remove marker when focus leaves main (user tabs to next element)
        const handleBlur = () => {
          removeMarker();
        };

        // Remove marker when user clicks anywhere on the page
        const handleDocumentClick = (e: MouseEvent) => {
          // Don't remove if clicking on the skip link itself
          const target = e.target as HTMLElement;
          if (!target.closest(".skip-link")) {
            removeMarker();
          }
        };

        // Listen for blur to remove border when user moves focus
        main.addEventListener("blur", handleBlur, { once: true });

        // Listen for clicks anywhere on the document to remove border
        document.addEventListener("click", handleDocumentClick, { once: true });
      });
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLAnchorElement>) => {
    // Handle Enter key activation
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      handleClick(event as unknown as React.MouseEvent<HTMLAnchorElement>);
    }
  };

  return (
    <a
      href="#main-content"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className="skip-link"
    >
      Skip to main content
    </a>
  );
}
